import { FormHeader } from "app/components/atoms/layout";
import { Select } from "app/components/atoms/select";
import Table from "app/components/atoms/table";
import { DateRangeInputMolecule } from "app/components/molecules/input";
import api from "app/integration/api";
import { useFetcher } from "app/integration/common/hooks";
import MainLayout from "app/layouts/main";
import { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { defaultReportDates } from "app/common/reportDateRange";

const breadcrumbItems = [
  { label: "Laporan Toko", href: "#" },
  { label: "Mutasi Stok Outlet", href: "#" },
];

// Kolom angka, urut sesuai rumus: Stok Awal + Masuk − Terjual − Keluar Lain = Sisa.
const qtyColumns = [
  { key: "opening", label: "Stok Awal" },
  { key: "qty_in", label: "Masuk" },
  { key: "qty_sold", label: "Terjual" },
  { key: "qty_out_other", label: "Keluar Lain" },
  { key: "closing", label: "Sisa Stok" },
];

/**
 * Laporan mutasi stok per varian dalam satu rentang tanggal, digabung untuk
 * semua outlet — filter Outlet untuk melihat satu outlet saja.
 * Hanya gudang outlet — Gudang Utama tidak punya riwayat stok (lihat
 * `StoreReportService.stockMutation` di backend).
 */
function StoreReportStockMutationPage() {
  const [warehouses, setWarehouses] = useState([]);
  const [queryParams, setQueryParams] = useState({
    warehouse_id: null,
    ...defaultReportDates(),
  });

  useEffect(() => {
    const loadWarehouses = async () => {
      try {
        const response = await api.warehouse.index({ limit: 1000 });
        setWarehouses(response?.data ?? []);
      } catch (err) {
        setWarehouses([]);
      }
    };
    loadWarehouses();
  }, []);

  const warehouseOptions = [
    { value: null, label: "Semua Outlet" },
    ...warehouses.map((warehouse) => ({
      value: warehouse.id,
      label: warehouse.name,
    })),
  ];

  const [data, , loader, , loadError] = useFetcher({
    modelName: "storeReport",
    methodName: "stockMutation",
    payload: { id: queryParams },
  });
  const rows = Array.isArray(data) ? data : [];

  const totals = qtyColumns.reduce((acc, { key }) => {
    acc[key] = rows.reduce((sum, row) => sum + Number(row[key] ?? 0), 0);
    return acc;
  }, {});

  const exportExcel = () => {
    const header = ["Kode Produk", "Produk", "Size", "Warna", ...qtyColumns.map((c) => c.label)];
    const dataForExcel = rows.map((row) => ({
      "Kode Produk": row.product_code,
      Produk: row.product_name,
      Size: row.size_name ?? "-",
      Warna: row.color_name ?? "-",
      ...Object.fromEntries(qtyColumns.map((c) => [c.label, Number(row[c.key] ?? 0)])),
    }));
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(dataForExcel, { header });
    worksheet["!cols"] = [
      { wch: 16 },
      { wch: 30 },
      { wch: 10 },
      { wch: 14 },
      ...qtyColumns.map(() => ({ wch: 12 })),
    ];
    XLSX.utils.book_append_sheet(workbook, worksheet, "Mutasi Stok");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `laporan_mutasi_stok_${queryParams.start_date}_${queryParams.end_date}.xlsx`;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 100);
  };

  return (
    <MainLayout
      activeSidebarNavigation="store-report"
      activeSidebarChild="stock-mutation"
      breadcrumbItems={breadcrumbItems}
      pageTitle="Laporan Mutasi Stok Outlet"
      headingButtons={[
        {
          label: "Export Excel",
          type: "button",
          onClick: exportExcel,
        },
      ]}
    >
      <Table.Container>
        <Table.SearchBarWrapper>
          <FormHeader>
            <div className="col-span-2">
              <DateRangeInputMolecule testId="store-report-stock-mutation-date-range"
                label="Tanggal"
                from={queryParams.start_date}
                to={queryParams.end_date}
                onFromChange={(e) =>
                  setQueryParams({ ...queryParams, start_date: e.target.value })
                }
                onToChange={(e) =>
                  setQueryParams({ ...queryParams, end_date: e.target.value })
                }
              />
            </div>
            <Select testId="store-report-stock-mutation-outlet-select"
              label="Outlet"
              placeholder="Filter Outlet"
              options={warehouseOptions}
              value={queryParams.warehouse_id}
              onChange={(e) =>
                setQueryParams({ ...queryParams, warehouse_id: e.target.value })
              }
              absolute={false}
            />
          </FormHeader>
        </Table.SearchBarWrapper>
        <p className="text-xs px-2 pt-2 text-gray-500 font-light">
          Stok Awal + Masuk − Terjual − Keluar Lain = Sisa Stok. Masuk: transfer
          diterima (termasuk transfer batal yang kembali), barang masuk/refill,
          stock opname lebih. Terjual: penjualan POS dikurangi transaksi yang
          di-void. Keluar Lain: transfer dikirim, barang keluar, stock opname
          kurang. Hanya gudang outlet; Gudang Utama tidak termasuk.
        </p>
        <Table.Wrapper asIndex={true}>
          <Table.Header>
            <Table.HeaderRow>
              <Table.Heading>Kode Produk</Table.Heading>
              <Table.Heading>Produk</Table.Heading>
              <Table.Heading>Size</Table.Heading>
              <Table.Heading>Warna</Table.Heading>
              {qtyColumns.map((c) => (
                <Table.Heading key={c.key}>
                  <p className="text-right">{c.label}</p>
                </Table.Heading>
              ))}
            </Table.HeaderRow>
          </Table.Header>
          <Table.Body>
            {rows.map((row) => (
              <Table.BodyRow key={row.product_variant_id}>
                <Table.Cell>{row.product_code}</Table.Cell>
                <Table.Cell>{row.product_name}</Table.Cell>
                <Table.Cell>{row.size_name ?? "-"}</Table.Cell>
                <Table.Cell>{row.color_name ?? "-"}</Table.Cell>
                {qtyColumns.map((c) => (
                  <Table.Cell key={c.key} fontWeight={c.key === "closing" ? "semibold" : undefined}>
                    <p className="text-right">{Number(row[c.key] ?? 0)}</p>
                  </Table.Cell>
                ))}
              </Table.BodyRow>
            ))}
            {rows.length > 0 && (
              <Table.BodyRow>
                <Table.Cell colSpan={4} fontWeight="semibold">
                  Total
                </Table.Cell>
                {qtyColumns.map((c) => (
                  <Table.Cell key={c.key} fontWeight="semibold">
                    <p className="text-right">{totals[c.key]}</p>
                  </Table.Cell>
                ))}
              </Table.BodyRow>
            )}
          </Table.Body>
        </Table.Wrapper>
      </Table.Container>
      {loader.state && (
        <p className="text-sm px-2 py-1 text-gray-500 font-light">
          Memuat data...
        </p>
      )}
      {loadError && !loader.state && (
        <p className="text-sm px-2 py-1 text-red-600" data-testid="report-load-error">
          Gagal memuat data: {loadError}
        </p>
      )}
      {rows.length === 0 && !loader.state && !loadError && (
        <p className="text-sm px-2 py-1 text-gray-500 font-light">
          Tidak ada data untuk filter ini.
        </p>
      )}
    </MainLayout>
  );
}

export default StoreReportStockMutationPage;
